import {
  DecimalPipe,
  formatDate,
  getCurrencySymbol,
  NgIf,
  NgClass,
  AsyncPipe,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import {
  PageToolbarComponent,
  ExtendedNotificationComponent,
  PrintPageComponent,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Discount } from 'src/app/settings/models/settings.model';
import { getDiscount } from 'src/app/settings/store/selectors/settings.selectors';

import { QuickSimulationItemComponent } from './quick-simulation-item-dialog/quick-simulation-item.component';
import { QuickSimulationReportComponent } from './quick-simulation-report/quick-simulation-report.component';
import {
  Calculation,
  QuickSimulation,
  QuickSimulationTableRow,
} from '../../models/feed-profit.model';
import { FeedProfitCalculations } from '../../services/feed-profit-calculations.service';
import { FeedProfitQuickSimulationsCalculationService } from '../../services/feed-profit-quick-simulations-calculation.service';
import * as actions from '../../store/feed-profit.actions';
import { getSelectedCalculation } from '../../store/feed-profit.selectors';

@Component({
  selector: 'app-quick-simulation',
  templateUrl: './quick-simulation.component.html',
  styleUrls: ['./quick-simulation.component.scss'],
  imports: [
    PageToolbarComponent,
    NgIf,
    ExtendedNotificationComponent,
    TableComponent,
    NgClass,
    MatIcon,
    MatIconButton,
    PrintPageComponent,
    QuickSimulationReportComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class QuickSimulationComponent implements AfterViewInit, OnInit {
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  calculationCount = 0;

  @ViewChild('fullWidthRow') fullWidthRow: TemplateRef<unknown>;
  @ViewChild('changeColumn') changeColumn: TemplateRef<unknown>;
  @ViewChild('deleteColumn') deleteColumn: TemplateRef<unknown>;
  @ViewChild('dataColumn') dataColumn: TemplateRef<unknown>;

  calculation: Calculation;
  quickSimulationsCanbeCalculated: boolean;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly feedProfitCalculationService: FeedProfitQuickSimulationsCalculationService,
    private readonly calculationService: FeedProfitCalculations,
    private readonly dialog: DialogService,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly decimalPipe: DecimalPipe,
  ) {}
  ngOnInit() {
    this.currencySymbol = this.currencySymbol = getCurrencySymbol(
      this.localeService.getCustomerCurrency(),
      'narrow',
    );
  }

  currencySymbol: string;
  comparisonSim: QuickSimulationTableRow;

  ngAfterViewInit() {
    this.store
      .select(getSelectedCalculation)
      .pipe(take(1))
      .subscribe((calculation: Calculation) => {
        if (!calculation) {
          this.router.navigate(['/feed-profit']).then();
          return;
        }
        this.calculation = calculation;
      });

    const newConfig = { ...this.tableConfigBase };
    newConfig.fullWidthRowTemplate = this.fullWidthRow;
    newConfig.fullWidthRowToggleColumn = 'showFullWidthColumn';
    this.tableConfigBase = newConfig;
    this.initTableConfig();
    this.changeDetectorRef.detectChanges();
  }

  updateStates(calculation: Calculation) {
    this.store.dispatch(
      actions.upsertCalculationRequest({
        calculation,
      }),
    );

    this.store.dispatch(
      actions.updateSelectedCalculation({
        values: {
          ...calculation,
        },
      }),
    );

    this.calculation = calculation;
    this.initTableConfig();
  }

  deleteFeedProfitSimulation(id: number): void {
    const newCalculation = {
      ...this.calculation,
      quickSimulations: this.calculation.quickSimulations.filter((sim) => sim.id !== id),
    };

    this.updateStates(newCalculation);
  }

  addQuickSimulation() {
    const dialogRef = this.dialog.fullscreenDialog(QuickSimulationItemComponent, {
      title: this.localeService.translate('feed-profit.quick-simulation-item.title'),
      subTitle: this.localeService.translate('feed-profit.quick-simulation-item.subtitle'),
      confirmButton: true,
      confirmButtonText: this.localeService.translate('save'),
      altButton: true,
      altButtonText: this.localeService.translate('save-and-create-new'),
      altButtonColor: 'primary',
      altButtonIcon: 'save',
      calculation: this.calculation,
    });

    dialogRef.subscribe((result) => {
      if (result.status === DIALOG_RETURN_TYPES.confirm) {
        const returnedCalculation = result.result.calculation
          ? result.result.calculation
          : this.calculation;
        this.store
          .select(getDiscount)
          .pipe(take(1))
          .subscribe((discounts) => {
            const newCalculation = <Calculation>{
              ...returnedCalculation,
              quickSimulations: [...returnedCalculation.quickSimulations, result.result.simulation],
              comparisonSimulation: this.mapComparisonSimulation(returnedCalculation, discounts),
            };
            this.updateStates(newCalculation);
          });
      }
    });
  }

  initTableConfig() {
    this.tableConfig$ = this.store.select(getDiscount).pipe(
      map((discounts: Discount) => {
        const tableConfig: TableConfig = {
          ...this.tableConfigBase,
        };
        tableConfig.columns = <TableColumn[]>[
          {
            id: 'startWeight',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.start-weight',
              ) + ' (kg)',
            digitsInfo: '1.01-01',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'pigletPrice',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.piglet-price',
              ) + ` (${this.currencySymbol})`,
            digitsInfo: '1.02-02',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'slaughteredWeightInKg',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.slaughtered-weight',
              ) + ' (kg)',
            digitsInfo: '1.01-01',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'revenuePerKg',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate('feed-profit.detail.quick-simulation.table.revenue') +
              ` (${this.currencySymbol})`,
            digitsInfo: '1.02-02',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'feedPackagePrice',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.feed-packet-price',
              ) + `(${this.currencySymbol})`,
            digitsInfo: '1.02-02',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'occupancyRate',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.occupancy-rate',
              ) + ' (%)',
            digitsInfo: '1.0-1',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'amount',
            type: TableColumnTypes.TEMPLATE,
            label: this.localeService.translate('feed-profit.detail.quick-simulation.table.amount'),
            digitsInfo: '1.0-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'growth',
            type: TableColumnTypes.TEMPLATE,
            label: this.localeService.translate('feed-profit.detail.quick-simulation.table.growth'),
            digitsInfo: '1.0-0',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'feedConversion',
            type: TableColumnTypes.TEMPLATE,
            label: this.localeService.translate(
              'feed-profit.detail.quick-simulation.table.feed-conversion',
            ),
            digitsInfo: '1.01-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'feedPrice',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate('feed-profit.detail.quick-simulation.table.feed-price') +
              ` (${this.currencySymbol})`,
            digitsInfo: '1.03-3',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'finalDifferenceDelivered',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.final-difference-delivered',
              ) + ` (${this.currencySymbol})`,
            digitsInfo: '1.02-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'finalDifferenceAveragePresentGrowFinisher',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.final-difference-average-present-grow-finisher',
              ) + ` (${this.currencySymbol})`,
            digitsInfo: '1.02-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'finalDifferenceYear',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.final-difference-year',
              ) + ` (${this.currencySymbol})`,
            digitsInfo: '1.02-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'feedProfitDelivered',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.feed-profit-delivered',
              ) + ` (${this.currencySymbol})`,
            digitsInfo: '1.02-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'feedProfitAverage',
            type: TableColumnTypes.TEMPLATE,
            label:
              this.localeService.translate(
                'feed-profit.detail.quick-simulation.table.feed-profit-average',
              ) + ` (${this.currencySymbol})`,
            digitsInfo: '1.02-2',
            disableSort: true,
            template: this.dataColumn,
          },
          {
            id: 'delete',
            type: TableColumnTypes.TEMPLATE,
            template: this.deleteColumn,
            disableSort: true,
            label: this.localeService.translate('action'),
          },
        ];
        this.quickSimulationsCanbeCalculated =
          this.calculationService.getQuickSimulationCanBeCalculated(this.calculation) ?? false;
        const updatedCalculation: Calculation = {
          ...this.calculation,
          comparisonSimulation: this.feedProfitCalculationService.getComparisonSimulation(
            this.calculation,
            discounts,
            false,
          ),
        };

        const allComparisonValuesFilledIn = Object.values(
          updatedCalculation.comparisonSimulation,
        ).every((val) => val !== undefined && val !== null);

        // If we have a comparison value, calculate it.
        if (allComparisonValuesFilledIn && this.quickSimulationsCanbeCalculated) {
          this.comparisonSim = this.feedProfitCalculationService.calculateSimulation(
            updatedCalculation,
            updatedCalculation.comparisonSimulation,
            0,
            discounts,
          );
        } else {
          this.comparisonSim = {
            ...updatedCalculation.comparisonSimulation,
            isComparisonSim: true,
          };
        }
        let simulations = [this.comparisonSim];
        if (this.calculation?.quickSimulations?.length > 0) {
          simulations = this.createCalculationsDatasource(
            updatedCalculation,
            this.quickSimulationsCanbeCalculated,
            discounts,
          );
        }

        tableConfig.dataSource = new MatTableDataSource(simulations);
        this.calculationsDatasource$ = of(simulations);
        return tableConfig;
      }),
    );
    this.changeDetectorRef.detectChanges();
  }

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  columns: TableColumn[] = undefined;

  private tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: this.columns,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
    width: '2800px',
  };

  public tableConfig$: Observable<TableConfig>;
  public calculationsDatasource$: Observable<QuickSimulationTableRow[]>;

  isPositive(text: string): number {
    const parsedText = parseFloat(text?.replace(',', '.')) * 100;
    return parsedText > 0 ? 1 : parsedText < 0 ? -1 : 0;
  }

  getValue(value: number, digitsInfo = '1.0-2') {
    if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
      return this.localeService.translate('n/a');
    }
    return this.decimalPipe.transform(value, digitsInfo, this.localeService.getLocale());
  }

  getDifferenceValue(row: QuickSimulationTableRow, column: TableColumn, digitsInfo = '1.0-2') {
    // If the row does not exist or is the comparison row, we do not have to calculate
    // the difference, it will always be null.
    if (!row || row.isComparisonSim || !this.comparisonSim) {
      return null;
    }

    // Find the value of the row column.
    const rowVal = Object.entries(row).find((rowVal) => rowVal[0] === column.id);

    // Do the same for the comparison row.
    const comparisonVal = Object.entries(this.comparisonSim).find(
      (rowVal) => rowVal[0] === column.id,
    );

    // Calculate the difference, use 0 if no value is available since we still want a number.
    const differenceVal = (rowVal ? rowVal[1] : 0) - (comparisonVal ? comparisonVal[1] : 0);

    return this.getValue(differenceVal, digitsInfo);
  }

  private createCalculationsDatasource(
    calculation: Calculation,
    simulationsCanbeCalculated: boolean,
    discounts: Discount,
  ) {
    let simulations: QuickSimulationTableRow[] = [];

    if (calculation.comparisonSimulation == null && calculation.quickSimulations?.length === 0) {
      this.calculationsDatasource$ = of([]);
      return [];
    }
    if (calculation.comparisonSimulation != null) {
      // Map the calculation to make sure all calculated properties are current
      const mappedCalculation = this.feedProfitCalculationService.generateMappedCalculation(
        calculation,
        calculation.comparisonSimulation,
        discounts,
      );

      // The mapped calculation object contains calculated values for everything,
      // used to calculate most current comparison to compare against.
      const comparisonSim = {
        ...mappedCalculation.comparisonSimulation,
        isComparisonSim: true,
        ...this.feedProfitCalculationService.calculateSimulation(
          mappedCalculation,
          mappedCalculation.comparisonSimulation,
          0,
          discounts,
        ),
      };

      this.comparisonSim = comparisonSim;
      simulations.push(this.comparisonSim);

      // add the comparison to our local calculation since it is needed for the non-comparison simulation calculations.
      calculation = {
        ...calculation,
        comparisonSimulation: comparisonSim,
      };
    }

    if (simulationsCanbeCalculated) {
      simulations = this.feedProfitCalculationService.calculateSimulations(calculation, discounts);
    } else {
      if (calculation.quickSimulations?.length > 0) {
        simulations = [
          ...simulations,
          ...calculation.quickSimulations.map(
            (sim) => <QuickSimulationTableRow>{ ...sim, isComparisonSim: false },
          ),
        ];
      }
    }

    // add show simulations tab
    if (simulations?.length > 0) {
      simulations[0].showFullWidthColumn = true;
      simulations[0].text = `${this.localeService.translate(
        'feed-profit.detail.quick-simulation.table.simulations-count-description',
      )} (${simulations.length - 1})`;
    }
    return simulations;
  }

  mapComparisonSimulation(calculation: Calculation, discounts: Discount): QuickSimulation {
    let comparisonSimulation = {
      startWeight: calculation.extendedBaseLine?.startWeight,
      pigletPrice: calculation.extendedBaseLine?.growFinisherPrice,
      slaughteredWeightInKg: calculation.extendedBaseLine?.slaughteredWeightInKg,
      revenuePerKg: calculation.extendedBaseLine?.revenuePerKg,
      feedPackagePrice: 0,
      calculatedFeedPackagePrice: 0,
    };
    const allComparisonValuesFilledIn = Object.values(comparisonSimulation).every(
      (val) => val !== undefined && val !== null,
    );

    // If we have a comparison value, calculate it.
    if (allComparisonValuesFilledIn && this.quickSimulationsCanbeCalculated) {
      // Map the calculation to make sure the calculated properties are current
      const mappedCalculation = this.feedProfitCalculationService.generateMappedCalculation(
        calculation,
        calculation.comparisonSimulation,
        discounts,
      );
      const packagePrice = this.feedProfitCalculationService.getPacketPrice(
        mappedCalculation,
        mappedCalculation.comparisonSimulation,
      );
      comparisonSimulation = {
        ...comparisonSimulation,
        calculatedFeedPackagePrice:
          packagePrice !== null &&
          packagePrice !== undefined &&
          !isNaN(packagePrice) &&
          isFinite(packagePrice)
            ? packagePrice
            : 0,
      };
    }
    return comparisonSimulation;
  }

  printQuickSimulations() {
    const existingTitle = document.title;
    const proposedTitle = `Pig ${this.localeService.translate(
      'feed-profit.detail.quick-simulation.title',
    )} - ${formatDate(Date(), 'dd-MM-yyyy', this.localeService.getLocale())}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }

  hasCalculations() {
    return this.calculationCount > 0;
  }
}
